import {
    useState,
    useEffect,
    useRef,
    useMemo,
    useCallback,
    MouseEventHandler,
} from "react";
import { SelectChangeEvent } from "@mui/material/Select";
import { TablePaginationProps } from "@mui/material/TablePagination";
import { useStore } from "@ui/hooks/useStore";
import useDictionary from "@ui/hooks/useDictionary";
import Telemetry, { ITelemetry, ITelemetry2 } from "@core/Telemetry";
import { IState } from "./IState";
import getSortComparator from "@utils/getSortComparator";
import {
    GridEventListener,
    GridRowParams,
    GridSortModel,
} from "@mui/x-data-grid";
import { gridSortDirections } from "@core/enums/grid-sort-directions";
import { rootStore } from "@store/Root.store";

export interface IColumn {
    renderCell?: any;
    field: string;
    headerName: string;
    sortComparator: (v1: any, v2: any) => number;
    //width: number;
    editable: boolean;
    resizable: boolean;
    minWidth: number;
    sortable: boolean;
    hideable: boolean;
    filterable: boolean;
}

export interface IRow {
    id: number;
    deviceName: string;
    imei: string;
    password: string;
    group: string;
}

const useDeviceTelemetry = () => {
    const getWord = useDictionary();
    const { app, devices, user, lang, map, telemetry, download } = useStore();
    const initState: IState = {
        page: 0,
        visibilitySettingsState: false,
        visibilitySettingsEl: null,
    };
    const [state, setState] = useState(initState);

    const visibilitySettingsRef = useRef<HTMLDivElement | null>(null);
    const refs = {
        visibilitySettingsRef,
    };

    const width: number[] = useMemo(
        // () => [
        //     130, 160, 160, 160, 110, 140, 200, 110, 180, 190, 190, 180, 180,
        //     210, 260, 160, 160, 160, 160, 160, 170, 170,
        // ],
        () => [
            5, 130, 160, 50, 160, 160, 110, 140, 200, 110, 180, 190, 190, 180, 180,
            160, 160, 160, 160, 160, 170, 170,
        ],
        []
    );
    const columns: IColumn[] = useMemo(
        () =>
            Telemetry.headers.map((field, i) => {
                const { units } = Telemetry;
                let headerName = getWord(field);
                const sortComparator = getSortComparator(field);
                if (field in units) {
                    const unit = getWord(units[field as keyof typeof units]);
                    headerName = `${headerName}, ${unit}`;
                }
                if (field === "address") {
                    width[i] =
                        telemetry.currentTelemetry.reduce((acc, data) => {

                            if (data.address && data.address.length > acc) {
                                return data.address.length;
                            } else {
                                return acc;
                            }
                        }, 0) * 7;
                }

                const result = {
                    field,
                    headerName,
                    sortComparator,
                    minWidth: width[i],
                    editable: false,
                    sortable: field === "date",
                    resizable: true,
                    hideable: true,
                    filterable: false,
                };

                if(field === "number"){
                    return {
                        field: field,
                        headerName: headerName,
                        sortComparator: sortComparator,
                        renderCell: (index: any) => index.api.getRowIndex(index.row.id) + 1,
                        minWidth: width[i],
                        editable: false,
                        sortable: false,
                        resizable: true,
                        hideable: false,
                        filterable: false,
                    }
                }

                return result;
            }),
        [
            lang.lang,
            telemetry.currentTelemetry,
            user.settings.timezone,
            lang.lang,
        ]
    );
    const rows: ITelemetry2[] = useMemo(
        () =>
            [...telemetry.currentTelemetry]
                .sort((a, b) => b.ts_s - a.ts_s)
                .map((data, i) => {
                    const id: number = i;
                    const telemetry: ITelemetry2 = new Telemetry(data, {
                        timezone: user.settings.timezone ?? 0,
                    }).getObject();
                    Object.keys(telemetry).forEach((key) => {
                        telemetry[key as keyof typeof telemetry] =
                            (telemetry[
                                key as keyof typeof telemetry
                            ] as never) ?? "—";
                    });                    
                    //telemetry.number = id + 1;
                    telemetry.coordType = telemetry.coordType
                        ? getWord(telemetry.coordType)
                        : "";
                    telemetry.event = getWord(telemetry.event);
                    telemetry.sim1pr = getWord(telemetry.sim1pr);
                    telemetry.sim2pr = getWord(telemetry.sim2pr);
                    telemetry.pm = getWord(telemetry.pm);
                    // telemetry.alarm1 = getWord(telemetry.alarm1);
                    // telemetry.alarm2 = getWord(telemetry.alarm2);
                    // telemetry.alarm3 = getWord(telemetry.alarm3);
                    // telemetry.alarm4 = getWord(telemetry.alarm4);
                    // telemetry.alarm5 = getWord(telemetry.alarm5);
                    // telemetry.alarm6 = getWord(telemetry.alarm6);
                    return {
                        ...telemetry,
                        id,
                    };
                }),
        [telemetry.currentTelemetry, user.settings.timezone, lang.lang]
    );

    const values = { columns, rows };
    useEffect(() => {
        telemetry.resetServerError();
    }, []);
    const handlePageChange: TablePaginationProps["onPageChange"] = useCallback(
        (e, page) => {
            setState((prev) => ({ ...prev, page }));
        },
        []
    );
    const handleDownloadClick = useCallback(async () => {
        const cvm = telemetry.columnVisibilityModel;
        let field: string | undefined;
        let sort: string | null | undefined;
        let dataRows: ITelemetry[];
        if (telemetry.sortModel.length > 0) {
            field = telemetry.sortModel[0].field;
            sort = telemetry.sortModel[0].sort;
        }
        const header = columns
            .filter((col) => cvm[col.field])
            .map((col) => col.headerName);
        const cols = columns
            .filter((col) => cvm[col.field])
            .map((col) => col.field);
        if (field) {
            const sortComparator = getSortComparator(field);
            if (sort === "asc")
                dataRows = [...rows].sort((a, b) => {
                    const v1 = a[field as keyof typeof a];
                    const v2 = b[field as keyof typeof b];
                    return sortComparator(v1, v2);
                });
            else if (sort === "desc")
                dataRows = [...rows].sort((a, b) => {
                    const v1 = a[field as keyof typeof a];
                    const v2 = b[field as keyof typeof b];
                    return sortComparator(v1, v2) * -1;
                });
            else dataRows = rows;
        } else dataRows = rows;
        const data = dataRows.map((row, idx) =>
            cols.map((col) => {
                if(col === "number") return idx + 1;
                return row[col as keyof typeof row]
            })
        );
        const filename = getWord("telemetry");
        await download.downloadCustomData(header, data, filename);
    }, [rows, columns, telemetry.columnVisibilityModel, telemetry.sortModel]);
    const handleClose = useCallback(() => {
        telemetry.resetTelemetryVisible();
    }, []);
    const handleRowClick: GridEventListener<"rowClick"> = useCallback(
        (params: GridRowParams<ITelemetry>) => {
            const coordsStr = params.row.coords;
            const dateStr = params.row.date;
            if (!coordsStr) return;
            const [lat, lon] = coordsStr
                .split(", ")
                .map((val) => parseFloat(val));
            if (Number.isNaN(lat) || Number.isNaN(lon)) return;
            map.setCurrentPosition([lat, lon]);
            map.setPosition({ lat, lon });
            map.setZoom(18);
            dateStr && map.setHighlightedCoords(coordsStr, dateStr);
        },
        []
    );
    const handleSortModelChange = useCallback((sortModel: GridSortModel) => {
        telemetry.setSortModel(sortModel);
    }, []);
    const handleUnsort = useCallback(() => {
        telemetry.resetSortModel();
    }, []);
    const handleAscSort = useCallback((field: string) => {
        const sortModel = [{ field, sort: gridSortDirections.asc }];
        telemetry.setSortModel(sortModel);
    }, []);
    const handleDescSort = useCallback((field: string) => {
        const sortModel = [{ field, sort: gridSortDirections.desc }];
        telemetry.setSortModel(sortModel);
    }, []);
    const handleHideCol = useCallback((field: string) => {
        telemetry.hideCol(field);
    }, []);
    const handleToggleCol: MouseEventHandler<HTMLElement> = useCallback((e) => {
        const target = e.target as HTMLElement;
        const field = target.id;
        if (field) telemetry.toggleCol(field);
    }, []);
    const handleShowAllCol = useCallback(() => {
        telemetry.showAllCol();
    }, []);
    const handleHideAllCol = useCallback(() => {
        telemetry.hideAllCol();
    }, []);
    const handleVisibilitySettingsStateChange = useCallback(() => {
        setState((prev) => ({
            ...prev,
            visibilitySettingsState: !prev.visibilitySettingsState,
            visibilitySettingsEl: visibilitySettingsRef.current,
        }));
    }, []);
    const handleVisibilitySettingsStateClose = useCallback(() => {
        setState((prev) => ({
            ...prev,
            visibilitySettingsState: false,
        }));
    }, []);
    const handeLimitChange = useCallback((e: SelectChangeEvent<number>) => {
        const limit = +e.target.value;
        setState((prev) => ({ ...prev, page: 0 }));
        telemetry.setLimit(limit);
    }, []);
    const handlers = {
        handlePageChange,
        handleDownloadClick,
        handleClose,
        handleRowClick,
        handleSortModelChange,
        handleUnsort,
        handleAscSort,
        handleDescSort,
        handleHideCol,
        handleToggleCol,
        handleVisibilitySettingsStateChange,
        handleVisibilitySettingsStateClose,
        handleShowAllCol,
        handleHideAllCol,
        handeLimitChange,
    };

    return {
        app,
        lang,
        devices,
        user,
        telemetry,
        state,
        refs,
        values,
        handlers,
    };
};

export default useDeviceTelemetry;
